import { assertNever, ProspectListStatus, type ProspectListRead } from '@signalvote/shared';
import { failedPartway, keptReasons } from '@/lib/fundraising/prospectListUpload';

const count = (n: number) => n.toLocaleString();

/** One upload's state in a sentence. */
export function ProspectListProgressLine({ list }: { list: ProspectListRead }) {
  switch (list.status) {
    case ProspectListStatus.Pending:
      return (
        <p className="text-sm text-muted-foreground">
          Waiting to start — {count(list.rowCount)} rows queued. You can leave this page.
        </p>
      );
    case ProspectListStatus.Importing:
      return (
        <p className="text-sm text-muted-foreground">
          Adding people — {count(list.rowsWatermark)} of {count(list.rowCount)} rows.
        </p>
      );
    case ProspectListStatus.Imported:
      return (
        <div className="space-y-1 text-sm">
          <p className="text-foreground">
            Done: {count(list.createdCount)} new people, {count(list.adoptedCount)} already in your book.
          </p>
          {list.failedCount > 0 && (
            <p className="text-amber-700">{count(list.failedCount)} rows could not be added.</p>
          )}
        </div>
      );
    case ProspectListStatus.Failed:
      // The same rule that offers Retry, so the button never sits beside copy that doesn't explain it.
      if (!failedPartway(list)) return <p className="text-sm text-amber-700">This upload didn&apos;t finish.</p>;
      return (
        <div className="space-y-1 text-sm">
          <p className="text-amber-700">
            {list.createdCount > 0
              ? `This upload stopped partway and added ${count(list.createdCount)} people.`
              : 'This upload stopped partway.'}
          </p>
          <p className="text-muted-foreground">
            Retry it to carry on from there, or undo it before uploading a corrected file.
          </p>
        </div>
      );
    case ProspectListStatus.Undone:
      if (!list.undo) return <p className="text-sm text-muted-foreground">This upload was undone.</p>;
      if (!list.undo.partlyUndone) {
        return (
          <p className="text-sm text-muted-foreground">Undone: {count(list.undo.removedCount)} people removed.</p>
        );
      }
      return (
        <div className="space-y-1 text-sm">
          <p className="text-foreground">
            Partly undone: {count(list.undo.removedCount)} people removed, {count(list.undo.keptCount)} kept.
          </p>
          <p className="text-muted-foreground">Kept: {keptReasons(list.undo)}.</p>
        </div>
      );
    default:
      return assertNever(list.status);
  }
}
